import type { NoteTypes, EntityTypes } from "db/types";
import { apiRequest } from "../http";

type NoteEntityWithEntity = EntityTypes.SerializedNoteEntityWithEntity;

/**
 * Fetch all notes with optional filters
 * Can be used server-side (pass request) or client-side
 */
export const fetchNotes = ({
  signal,
  type,
  request,
}: {
  signal?: AbortSignal;
  type?: string;
  request?: Request;
} = {}) => {
  const params = new URLSearchParams();
  if (type) params.append("type", type);

  const query = params.toString() ? `?${params}` : "";
  return apiRequest<NoteTypes.SerializedNote[]>(`/api/notes${query}`, {
    signal,
    request,
  });
};

/**
 * Fetch home page filtered notes with server-computed dates
 */
export const fetchHomeNotes = ({
  signal,
  request,
}: {
  signal?: AbortSignal;
  request?: Request;
} = {}) => {
  return apiRequest<{
    overdueNotes: NoteTypes.SerializedNote[];
    dueTodayNotes: NoteTypes.SerializedNote[];
    recentlyAdded: NoteTypes.SerializedNote[];
    overdueLists: NoteListWithNotes[];
    dueTodayLists: NoteListWithNotes[];
    upcomingLists: NoteListWithNotes[];
  }>(`/api/notes/home`, {
    signal,
    request,
  });
};

export interface NoteListWithNotes {
  id: string;
  name: string;
  description: string | null;
  icon: string | null;
  dueDate: string | null;
  listType: string | null;
  isSystem: boolean;
  notes: Array<{
    id: string;
    noteListId: string;
    noteId: string;
    properties: Record<string, unknown>;
    note: {
      id: string;
      title: string | null;
      content: string | null;
      status: string;
      completed: boolean | null;
    };
  }>;
  _count: {
    notes: number;
  };
}

/**
 * Fetch a single note by ID
 */
export const fetchNote = (
  id: string,
  { signal }: { signal?: AbortSignal } = {},
) =>
  apiRequest<NoteTypes.SerializedNoteWithEntities>(`/api/notes/${id}`, {
    signal,
  });

/**
 * Fetch notes by type
 */
export const fetchNotesByType = (type: string) =>
  apiRequest<NoteTypes.SerializedNote[]>(`/api/notes/type/${type}`);

/**
 * Create a new note
 */
export const createNote = (input: {
  title?: string;
  content?: string;
  type?: string;
  metadata?: {
    sourceUrl?: string;
    imageUrl?: string;
    siteName?: string;
    contentType?: string;
    metadataType: string;
    data?: any;
  };
  collectionIds?: string[];
}) =>
  apiRequest<NoteTypes.SerializedNote>("/api/notes", {
    method: "POST",
    body: input,
  });

/**
 * Update an existing note
 */
export const updateNote = (data: {
  id: string;
  title?: string;
  content?: string;
  type?: string;
  status?: string;
  dueDate?: string | null;
  inboxStatus?: string;
}) =>
  apiRequest<NoteTypes.SerializedNote>("/api/notes", {
    method: "PUT",
    body: data,
  });

/**
 * Delete a note
 */
export const deleteNote = (id: string) =>
  apiRequest<void>(`/api/notes/${id}`, {
    method: "DELETE",
  });

/**
 * Fetch entities associated with a note
 */
export const fetchNoteEntities = (noteId: string) =>
  apiRequest<NoteEntityWithEntity[]>(`/api/notes/${noteId}/entities`);

/**
 * Fetch activity/interactions for a note
 */
export const fetchNoteActivity = (noteId: string) =>
  apiRequest(`/api/notes/${noteId}/activity`);

/**
 * Delete all entities from a note
 */
export const deleteNoteEntities = (noteId: string) =>
  apiRequest(`/api/notes/${noteId}/entities`, {
    method: "DELETE",
  });

/**
 * Regenerate entities for a note
 */
export const regenerateNoteEntities = (noteId: string) =>
  apiRequest(`/api/notes/${noteId}/entities/regenerate`, {
    method: "POST",
  });

export interface NoteComment {
  id: string;
  content: string;
  createdAt: string;
  updatedAt: string;
  user: {
    id: string;
    name: string | null;
    email: string;
    profilePicture: string | null;
  };
}

/**
 * Fetch comments for a note
 */
export const fetchNoteComments = (noteId: string) =>
  apiRequest<NoteComment[]>(`/api/notes/${noteId}/comments`);

/**
 * Create a comment on a note
 */
export const createNoteComment = (noteId: string, data: { content: string }) =>
  apiRequest(`/api/notes/${noteId}/comments`, {
    method: "POST",
    body: data,
  });

/**
 * Update a comment
 */
export const updateNoteComment = (
  noteId: string,
  commentId: string,
  data: { content: string },
) =>
  apiRequest(`/api/notes/${noteId}/comments/${commentId}`, {
    method: "PUT",
    body: data,
  });

/**
 * Delete a comment
 */
export const deleteNoteComment = (noteId: string, commentId: string) =>
  apiRequest(`/api/notes/${noteId}/comments/${commentId}`, {
    method: "DELETE",
  });

/**
 * Detect note type from content
 */
export const detectNoteType = (noteId: string) =>
  apiRequest("/api/detect-note-type", {
    method: "POST",
    body: { noteId },
  });

/**
 * Classify note content
 */
export const classifyNote = (text: string) =>
  apiRequest("/api/classify-note", {
    method: "POST",
    body: { text },
  });

/**
 * Generate todo instructions
 */
export const generateTodoInstructions = (data: {
  todoText: string;
  noteId?: string;
}) =>
  apiRequest("/api/generate-todo-instructions", {
    method: "POST",
    body: data,
  });

/**
 * Generate enrichment metadata
 */
export const generateEnrichment = (data: {
  noteContent: string;
  noteId: string;
}) =>
  apiRequest("/api/generate-enrichment", {
    method: "POST",
    body: data,
  });

/**
 * Process note entities (v2 with LangFuse observability)
 */
export const processNoteEntities = (data: {
  noteId: string;
  userId?: string;
  metadata?: any;
}) =>
  apiRequest("/api/v2/process-note-entities", {
    method: "POST",
    body: {
      noteId: data.noteId,
      userId: data.userId || "anonymous",
      metadata: data.metadata,
    },
  });

/**
 * Process note entities (v3 with content blocks support)
 */
export const processNoteEntitiesV3 = (data: {
  noteId: string;
  userId: string;
  processBlocks?: boolean;
}) =>
  apiRequest("/api/v3/process-note-entities", {
    method: "POST",
    body: data,
  });

/**
 * Generate AI content for a note
 */
export const generateAiContent = (data: {
  noteId: string;
  userId: string;
  contentType?: "instructions" | "summary" | "analysis" | "suggestions";
  blockType?: string;
  position?: "start" | "end" | number;
}) =>
  apiRequest("/api/v3/generate-ai-content", {
    method: "POST",
    body: data,
  });

/**
 * Create link metadata for a note
 */
export const createLinkMetadata = (data: {
  url: string;
  title?: string;
  description?: string;
  imageUrl?: string;
  noteId: string;
}) =>
  apiRequest("/api/link-metadata", {
    method: "POST",
    body: data,
  });

/**
 * Unfurl URL to get metadata
 */
export const unfurlUrl = (url: string) =>
  apiRequest(`/api/unfurl?url=${encodeURIComponent(url)}`);

/**
 * @deprecated Use NoteList queries instead. This endpoint will be removed after 2025-06-01.
 */
export const getSubtasks = (noteId: string) =>
  apiRequest(`/api/notes/${noteId}/subtasks`);
